import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { FormProvider, useForm } from 'react-hook-form';

import {
  getCoursePolicyOptions,
  getCoursePolicyQueryKey,
  updateCoursePolicyMutation,
} from '@drivingdoc/api-client/generated/react-query';
import { type MinimalUser } from 'api';

import CheckBoxInput from 'components/FormInputs/CheckBoxInput';
import IntervalInput from 'components/FormInputs/IntervalInput';
import CurrencyInput from 'components/Forms/CurrencyInput';

import { getApiErrorMessage } from 'utils/getApiErrorMessage.ts';
import normalizePgInterval from 'utils/normalizePgInterval.ts';

import DateModified from '../../components/DateModified.tsx';
import DialogFormSection from '../../components/DialogFormSection.tsx';
import FeedbackDialog from '../../FeedbackDialog';
import CourseFeeInput from './components/CourseFeeInput.tsx';
import { FormInputValues, FormOutputValues, FormSchema } from './formSchema.ts';

dayjs.extend(calendar);

interface UpdateCoursePolicyDialogPayload {
  /**
   * The course id to update
   */
  courseId: string;
}

export function UpdateCoursePolicyDialog({ payload, open, onClose }: DialogProps<UpdateCoursePolicyDialogPayload>) {
  const queryClient = useQueryClient();
  const dialogs = useDialogs();

  const updateCoursePolicy = useMutation({ ...updateCoursePolicyMutation() });

  const coursePolicy = useQuery({
    ...getCoursePolicyOptions({
      path: { id: payload.courseId },
    }),
    enabled: !!payload.courseId,
  });

  const form = useForm<FormInputValues, unknown, FormOutputValues>({
    mode: 'all',
    resolver: zodResolver(FormSchema),
    values: {
      min_time_to_complete: normalizePgInterval(coursePolicy.data?.min_time_to_complete),
      max_time_to_complete: normalizePgInterval(coursePolicy.data?.max_time_to_complete),
      allow_self_service_extensions: coursePolicy.data?.allow_self_service_extensions ?? false,
      standard_extension_time: normalizePgInterval(coursePolicy.data?.standard_extension_time),
      standard_extension_fee: coursePolicy.data?.standard_extension_fee ?? 0,
      max_extensions: coursePolicy.data?.max_extensions ?? 0,
      max_time_to_request_extension: normalizePgInterval(coursePolicy.data?.max_time_to_request_extension),
      absences_allowed_before_fee: coursePolicy.data?.absences_allowed_before_fee ?? 0,
      absence_fee: coursePolicy.data?.absence_fee ?? 0,
      absence_fee_due: normalizePgInterval(coursePolicy.data?.absence_fee_due),
      absence_fee_due_lock: !!coursePolicy.data?.absence_fee_due_lock,
      no_permits_allowed_before_fee: coursePolicy.data?.no_permits_allowed_before_fee ?? 0,
      no_permit_fee: coursePolicy.data?.no_permit_fee ?? 0,
      no_permit_fee_due: normalizePgInterval(coursePolicy.data?.no_permit_fee_due),
      no_permit_fee_due_lock: !!coursePolicy.data?.no_permit_fee_due_lock,
      allow_cancellations: coursePolicy.data?.allow_cancellations ?? false,
      cancellations_allowed_before_fee: coursePolicy.data?.cancellations_allowed_before_fee ?? 0,
      cancellation_fee: coursePolicy.data?.cancellation_fee ?? 0,
      cancellation_fee_due: normalizePgInterval(coursePolicy.data?.cancellation_fee_due),
      cancellation_fee_due_lock: !!coursePolicy.data?.cancellation_fee_due_lock,
      late_cancellations_allowed_before_fee: coursePolicy.data?.late_cancellations_allowed_before_fee ?? 0,
      late_cancellation_fee: coursePolicy.data?.late_cancellation_fee ?? 0,
      late_cancellation_fee_due: normalizePgInterval(coursePolicy.data?.late_cancellation_fee_due),
      late_cancellation_fee_due_lock: !!coursePolicy.data?.late_cancellation_fee_due_lock,
      late_cancellation_deadline: normalizePgInterval(coursePolicy.data?.late_cancellation_deadline),
      issue_completion_certificate: coursePolicy.data?.issue_completion_certificate ?? false,
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onSubmit = async (data: FormOutputValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm('All existing students will use this new policy.', {
        okText: 'Update',
        cancelText: 'Cancel',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      const result = await updateCoursePolicy.mutateAsync({
        path: { id: payload.courseId },
        body: data,
      });

      if (result) {
        // close the dialog
        handleClose();

        // invalidate the query key
        queryClient.invalidateQueries({
          queryKey: getCoursePolicyQueryKey({ path: { id: payload.courseId } }),
        });

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Policy Updated',
        });
      }
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error ',
        message: getApiErrorMessage(error),
      });
    }
  };

  return (
    <Dialog fullWidth open={open} onClose={handleClose}>
      <DialogTitle>Update Course Policy</DialogTitle>

      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <DialogContent>
            <DialogFormSection title="Maximum Time To Complete Course">
              <Grid size={12}>
                <IntervalInput name="max_time_to_complete" years months weeks days />
              </Grid>
            </DialogFormSection>

            <DialogFormSection title="Course Extension Settings">
              <Grid size={6}>
                <TextField
                  label="Max Extensions Allowed"
                  variant="standard"
                  type="number"
                  fullWidth
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  error={!!errors.max_extensions}
                  helperText={errors.max_extensions?.message}
                  {...register('max_extensions', { valueAsNumber: true })}
                />
              </Grid>
              <Grid size={6}>
                <CurrencyInput name="standard_extension_fee" label="Extension Fee" />
              </Grid>
              <Grid size={12}>
                <IntervalInput name="standard_extension_time" label="Extension Length" years months weeks days />
              </Grid>
              <Grid size={12}>
                <IntervalInput
                  name="max_time_to_request_extension"
                  label="Maximum Time To Request An Extension"
                  years
                  months
                  weeks
                  days
                />
              </Grid>
              <Grid size={12}>
                <CheckBoxInput name="allow_self_service_extensions" label="Allow Self-Service Extensions" />
              </Grid>
            </DialogFormSection>

            <DialogFormSection title="Course Fees">
              <Grid size={12}>
                <CourseFeeInput
                  label="No-Show Fee"
                  amountFieldName="absence_fee"
                  dueProps={register('absence_fee_due.days', { valueAsNumber: true })}
                  numAllowedProps={register('absences_allowed_before_fee', { valueAsNumber: true })}
                  lockWhenOverdueProps={register('absence_fee_due_lock')}
                />
              </Grid>
              <Grid size={12}>
                <CourseFeeInput
                  label="No Permit Fee"
                  amountFieldName="no_permit_fee"
                  dueProps={register('no_permit_fee_due.days', { valueAsNumber: true })}
                  numAllowedProps={register('no_permits_allowed_before_fee', { valueAsNumber: true })}
                  lockWhenOverdueProps={register('no_permit_fee_due_lock')}
                />
              </Grid>
              <Grid size={12}>
                <CourseFeeInput
                  label="Cancellation Fee"
                  amountFieldName="cancellation_fee"
                  dueProps={register('cancellation_fee_due.days', { valueAsNumber: true })}
                  numAllowedProps={register('cancellations_allowed_before_fee', { valueAsNumber: true })}
                  lockWhenOverdueProps={register('cancellation_fee_due_lock')}
                />
              </Grid>
              <Grid size={12}>
                <CourseFeeInput
                  label="Late Cancellation Fee"
                  amountFieldName="late_cancellation_fee"
                  dueProps={register('late_cancellation_fee_due.days', { valueAsNumber: true })}
                  numAllowedProps={register('late_cancellations_allowed_before_fee', { valueAsNumber: true })}
                  lockWhenOverdueProps={register('late_cancellation_fee_due_lock')}
                />
              </Grid>
            </DialogFormSection>

            <DialogFormSection title="Cancellation Settings" gridProps={{ justifyContent: 'flex-start' }}>
              <Grid size={12}>
                <IntervalInput name="late_cancellation_deadline" label="Late Cancel Deadline" days hours minutes />
              </Grid>
              <Grid size={12}>
                <CheckBoxInput name="allow_cancellations" label="Allow Cancellations" />
              </Grid>
            </DialogFormSection>

            <DialogFormSection title="Certificate Settings" gridProps={{ justifyContent: 'flex-start' }}>
              <Grid size={12}>
                <CheckBoxInput name="issue_completion_certificate" label="Automatically Issue Completion Certificate" />
              </Grid>
            </DialogFormSection>

            <Box pt={2}>
              <DateModified
                prefix="Updated: "
                date={coursePolicy.data?.updated_at}
                user={coursePolicy.data?.updated_by_user as MinimalUser}
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit">Update</Button>
          </DialogActions>
        </FormProvider>
      </form>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={updateCoursePolicy.isPending}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

export default UpdateCoursePolicyDialog;
